// TableC component
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Box,
  Typography,
  Grid,
  Stack,
  Chip,
  TableFooter,
  useMediaQuery
} from '@mui/material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTable, useFilters, useRowSelect, usePagination, useGlobalFilter, useSortBy, Column, HeaderGroup, Row, Cell } from 'react-table';
import { styled } from '@mui/material/styles';
import { HeaderSort, EmptyTable, DraggableRow, TableRowSelection } from 'components/third-party/ReactTable';
import { GlobalFilter } from 'utils/react-table';
import CombinedPagination from 'components/tables/CombinedPagination';
import ScrollX from 'components/ScrollX';
import TableSkeleton from 'components/skeletons/TableSkeleton';
import OverflowText from './OverflowText';
import update from 'immutability-helper';
import { Checkbox } from '@mui/material';
import { useUserSettings } from 'hooks/useUserSettings';

interface TableCProps {
  columns: Column<any>[];
  data: any[];
  title?: string;
  createButton: React.ReactNode;
  showGlobalFilter: boolean;
  showPagination: boolean;
  loading?: boolean;
  emptyTableMessage?: string;
  selectedRowIds?: Record<number, boolean>; // Add this to the interface
  onRecordsUpdate: (recordsData: any[]) => void; // Callback to pass updated records
  onSelectBoxChange: (selectRecordIDs: Record<number, boolean>) => void;
}

const Wrapper = styled('div')(({ theme }) => ({
  '.header': {
    position: 'sticky',
    zIndex: 1,
    width: 'fit-content'
  },
  '& th[data-sticky-td]': {
    position: 'sticky',
    zIndex: '5 !important'
  },
  '.MuiTableFooter-root': {
    position: 'sticky',
    bottom: 0,
    background: theme.palette.background.paper
  }
}));

/**
 * TableC component displays a table with expandable rows and pagination.
 *
 * @component
 * @param {TableCProps} props - The props for the TableC component.
 * @returns {JSX.Element} The rendered TableC component.
 */
const TableC: React.FC<TableCProps> = ({
  columns,
  data,
  title,
  createButton,
  showGlobalFilter,
  showPagination,
  loading = false,
  emptyTableMessage = 'No Data',
  selectedRowIds = {},
  onRecordsUpdate,
  onSelectBoxChange
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(new Set());
  const [availableWidth, setAvailableWidth] = useState(0);
  const tableRef = useRef<HTMLTableElement>(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [records, setRecords] = useState(data);
    const { userSettings } = useUserSettings();

  const updateAvailableWidth = () => {
    if (tableRef.current) {
      setAvailableWidth(tableRef.current.clientWidth);
    }
  };

  useEffect(() => {
    updateAvailableWidth();
    window.addEventListener('resize', updateAvailableWidth);
    return () => {
      window.removeEventListener('resize', updateAvailableWidth);
    };
  }, []);

  useEffect(() => {
    setRecords(data);
  }, [data]);

  useEffect(() => {
    updateAvailableWidth();
  }, [loading, data]);

  const toggleRow = (index: number) => {
    setExpandedRows((prevExpandedRows) => {
      const newExpandedRows = new Set(prevExpandedRows);
      if (newExpandedRows.has(index)) {
        newExpandedRows.delete(index);
      } else {
        newExpandedRows.add(index);
      }
      return newExpandedRows;
    });
  };

  const toggleColumn = (accessor: string) => {
    setExpandedColumns((prevExpandedColumns) => {
      const newExpandedColumns = new Set(prevExpandedColumns);
      if (newExpandedColumns.has(accessor)) {
        newExpandedColumns.delete(accessor);
      } else {
        newExpandedColumns.add(accessor);
      }
      return newExpandedColumns;
    });
  };
  const sorting = columns
    .filter((col) => (col as any).defaultSortOrder)
    .map((col) => ({
      id: col.accessor as string,
      desc: (col as any).defaultSortOrder === 'desc'
    }));

  const defaultColumn = useMemo(
    () => ({
      minWidth: 50,
      width: 100,
      maxWidth: 400,
      Cell: OverflowText
    }),
    []
  );
  const userPageSize = useMemo(() => {
    const size = Number(userSettings.DefaultRecordsPerPage);
    return !isNaN(size) && size > 0 ? size : 10;
  }, [userSettings.DefaultRecordsPerPage]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    gotoPage,
    preGlobalFilteredRows,
    setPageSize,
    page,
    pageCount,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter, selectedRowIds: internalSelectedRowIds },
    toggleRowSelected
  } = useTable(
    {
      data: records,
      columns,
      defaultColumn,
      disableSortRemove: true,
      initialState: {
        pageIndex: 0,
        pageSize: userPageSize,
        selectedRowIds,
        sortBy: sorting
      }
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (data.length > 0) {
        // Only include draggable row if there is data
        hooks.allColumns.push((columns: Column[]) => [
          {
            id: 'row-selection-chk',
            accessor: 'Selection',
            disableSortBy: true,
            className: 'row-selection-chk-TableC',
            width: 40,
            maxWidth: 50,
            Header: 'Select to Export',
            Cell: ({ row }: any) => (
              <Checkbox
                checked={row.original.isExport || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setRecords((prevRecords) =>
                    prevRecords.map((record, index) => (index === row.index ? { ...record, isExport: isChecked } : record))
                  );
                }}
              />
            )
          },
          ...columns
        ]);
      }
    }
  );
  useEffect(() => {
    if (userPageSize) {
      setPageSize(pageSize);
      gotoPage(0); // optional: reset to first page
    }
  }, [userPageSize, setPageSize, gotoPage]);

  useEffect(() => {
    const updatedSelection: Record<number, boolean> = {};
    records.forEach((row, index) => {
      if (row.isExport) {
        updatedSelection[index] = true;
      }
    });
    onSelectBoxChange(updatedSelection);
  }, [records]);
  useEffect(() => {
    const updatedSelection: Record<number, boolean> = {};
    records.forEach((row, index) => {
      toggleRowSelected(String(index), !!selectedRowIds[index]);
      if (row.isExport) {
        updatedSelection[index] = true;
      }
    });
  }, [selectedRowIds, records, toggleRowSelected]);
  useEffect(() => {
    // Send back updated records with selection status
    const updatedRecords = records.map((record, index) => ({
      ...record,
      isSelected: record.isExport
    }));

    onRecordsUpdate(updatedRecords);
  }, [internalSelectedRowIds, records, onRecordsUpdate]);

  const moveRow = (dragIndex: number, hoverIndex: number) => {
    const dragRecord = records[dragIndex];
    setRecords(
      update(records, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRecord]
        ]
      })
    );
  };

  const getVisibleItems = (items: any[]) => {
    const itemWidth = 400; // Estimated width of each item including margin
    const itemsThatFit = Math.floor((availableWidth - 200) / itemWidth); // Subtracting 200 to account for other cell content
    return items.slice(0, itemsThatFit);
  };

  return (
    <Grid container>
      <Grid container className="pb-15">
        <Grid item xs={12} md={6}>
          <Typography variant="h4">{title}</Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: { xs: 1 }
          }}
          gap={2}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <TableRowSelection selected={Object.keys(internalSelectedRowIds).length} />
            {showGlobalFilter && (
              <GlobalFilter preGlobalFilteredRows={preGlobalFilteredRows} globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
            )}
            {createButton}
          </Stack>
        </Grid>
      </Grid>
      <Grid container>
        {loading ? (
          <TableSkeleton columns={columns} />
        ) : (
          <ScrollX sx={{ minHeight: 50, maxHeight: 650 }}>
            <Wrapper>
              <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
                <Table {...getTableProps()} className="TableComp" ref={tableRef}>
                  <TableHead sx={{ borderTopWidth: 2 }}>
                    {headerGroups.map((headerGroup: HeaderGroup<any>) => (
                      <TableRow {...headerGroup.getHeaderGroupProps()}>
                        {page.length > 0 && <TableCell size="small" />}

                        {headerGroup.headers.map((column) => {
                          const isMobileColumn = isMobile && (column as any).showOnMobile;
                          const isVisible = !isMobile || isMobileColumn || expandedColumns.has(column.id);
                          let _className = column.headerClassName;
                          if (column.className) {
                            _className = _className ? `${_className} ${column.className}` : column.className;
                          }
                          return isVisible ? (
                            <TableCell
                              {...column.getHeaderProps([{ className: _className }])}
                              sx={{ width: column.width, position: 'sticky' }}
                            >
                              <HeaderSort column={column} sort />
                              {isMobile && !isMobileColumn && (
                                <IconButton size="small" onClick={() => toggleColumn(column.id)}>
                                  {expandedColumns.has(column.id) ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              )}
                            </TableCell>
                          ) : null;
                        })}
                        {isMobile && <TableCell />}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody {...getTableBodyProps()}>
                    {page.length > 0 ? (
                      page.map((row: Row<any>, i: number) => {
                        prepareRow(row);
                        return (
                          <React.Fragment key={i}>
                            <DraggableRow index={i} moveRow={moveRow} {...row.getRowProps()}>
                              {row.cells.map((cell: Cell<any>) => {
                                const isMobileColumn = isMobile && (cell.column as any).showOnMobile;
                                const isVisible = !isMobile || isMobileColumn || expandedColumns.has(cell.column.id);
                                const isCollapsible = (cell.column as any).isCollapsible;
                                const cellValue = cell.value;

                                return isVisible ? (
                                  <TableCell {...cell.getCellProps([{ className: cell.column.className }])}>
                                    {isCollapsible ? (
                                      <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <div style={{ flexGrow: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                                            {getVisibleItems(cellValue).map((chipValue: any, index: number) => (
                                              <Chip
                                                key={index}
                                                variant="outlined"
                                                label={chipValue.functionModuleName || chipValue}
                                                style={{ margin: '2px' }}
                                              />
                                            ))}
                                            {cellValue.length > getVisibleItems(cellValue).length && !expandedRows.has(i) && (
                                              <Typography variant="body2" color="primary" component="span">
                                                {' '}
                                                +{cellValue.length - getVisibleItems(cellValue).length} more
                                              </Typography>
                                            )}
                                          </div>
                                          {cellValue.length > getVisibleItems(cellValue).length && (
                                            <IconButton size="small" className="Collapsible-Button" onClick={() => toggleRow(i)}>
                                              {expandedRows.has(i) ? <ExpandLess /> : <ExpandMore />}
                                            </IconButton>
                                          )}
                                        </div>
                                        <Collapse in={expandedRows.has(i)} timeout="auto" unmountOnExit>
                                          <Box>
                                            <Grid container>
                                              {cellValue.slice(getVisibleItems(cellValue).length).map((chipValue: any, index: number) => (
                                                <Grid item key={index}>
                                                  <Chip
                                                    variant="outlined"
                                                    label={chipValue.functionModuleName || chipValue}
                                                    style={{ margin: '2px' }}
                                                  />
                                                </Grid>
                                              ))}
                                            </Grid>
                                          </Box>
                                        </Collapse>
                                      </>
                                    ) : (
                                      cell.render('Cell')
                                    )}
                                  </TableCell>
                                ) : null;
                              })}
                              {isMobile && (
                                <TableCell>
                                  <IconButton size="small" onClick={() => toggleRow(i)}>
                                    {expandedRows.has(i) ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                </TableCell>
                              )}
                            </DraggableRow>
                            {isMobile && expandedRows.has(i) && (
                              <TableRow>
                                <TableCell colSpan={columns.length + 1}>
                                  <Collapse in={expandedRows.has(i)} timeout="auto" unmountOnExit>
                                    <Box mt={1} ml={-1}>
                                      <Grid container spacing={1}>
                                        {row.cells.map((cell: Cell<any>) => (
                                          <Grid item key={cell.column.id} xs={12}>
                                            <Typography variant="body2" color="textSecondary">
                                              {typeof cell.column.Header === 'string' ? cell.column.Header : cell.column.render('Header')}
                                            </Typography>
                                            {cell.render('Cell')}
                                          </Grid>
                                        ))}
                                      </Grid>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <EmptyTable msg={emptyTableMessage} colSpan={columns.length + 1} />
                    )}
                  </TableBody>
                  {showPagination && !loading && data.length > 5 && (
                    <>
                      <TableFooter>
                        <TableRow className="tablePagination">
                          <TableCell colSpan={columns.length + 2}>
                            <CombinedPagination
                              gotoPage={gotoPage}
                              pageCount={pageCount}
                              pageIndex={pageIndex}
                              rows={page}
                              pageSize={pageSize}
                              setPageSize={setPageSize}
                            />
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </>
                  )}
                </Table>
              </DndProvider>
            </Wrapper>
          </ScrollX>
        )}
      </Grid>
    </Grid>
  );
};

export default TableC;
